import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  CheckCircle2, 
  MessageSquare, 
  FileText, 
  Mail, 
  MessageCircle,
  Megaphone, 
  Image as ImageIcon,
  Loader2,
  AlertTriangle,
  Check,
  Send,
  ChevronDown,
  Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FunnelPreview = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [activeSection, setActiveSection] = useState('landings');
  const [expandedItems, setExpandedItems] = useState({});
  const [comments, setComments] = useState({});
  const [approving, setApproving] = useState({});
  const [allApproved, setAllApproved] = useState(false);
  
  // Refs for scroll
  const sectionRefs = {
    landings: useRef(null),
    emails: useRef(null),
    whatsapp: useRef(null),
    ads: useRef(null),
    creatives: useRef(null)
  };

  useEffect(() => {
    const fetchPreview = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/preview/${token}`);
        setPreviewData(response.data);
        
        const approvals = response.data.approvals || {};
        const sections = ['landings', 'emails', 'ads', 'creatives'];
        const allApprovedCheck = sections.every(s => approvals[s]?.approved);
        setAllApproved(allApprovedCheck);
      } catch (err) {
        if (err.response?.status === 404) {
          setError('Este enlace no es válido o no existe.');
        } else if (err.response?.status === 410) {
          setError('Este enlace ha expirado. Solicita uno nuevo a tu agencia.');
        } else {
          setError('Error al cargar el preview. Intenta de nuevo más tarde.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchPreview();
    }
  }, [token]);

  const handleApprove = async (section) => {
    setApproving(prev => ({ ...prev, [section]: true }));
    
    try {
      await axios.post(`${API_URL}/api/preview/${token}/approve`, {
        section,
        approved: true,
        comment: comments[section] || null
      });
      
      setPreviewData(prev => ({
        ...prev,
        approvals: {
          ...prev.approvals,
          [section]: { approved: true, approved_at: new Date().toISOString() }
        }
      }));
      
      const updatedApprovals = {
        ...previewData.approvals,
        [section]: { approved: true }
      };
      const sections = ['landings', 'emails', 'ads', 'creatives'];
      const allApprovedCheck = sections.every(s => updatedApprovals[s]?.approved);
      setAllApproved(allApprovedCheck);
      
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Error approving section:', err);
    } finally {
      setApproving(prev => ({ ...prev, [section]: false }));
    }
  };

  const handleComment = async (section) => {
    if (!comments[section]?.trim()) return;
    
    try {
      await axios.post(`${API_URL}/api/preview/${token}/approve`, {
        section,
        approved: false,
        comment: comments[section]
      });
      
      setComments(prev => ({ ...prev, [section]: '' }));
      
      setPreviewData(prev => ({
        ...prev,
        comments: [
          ...(prev.comments || []),
          { section, comment: comments[section], created_at: new Date().toISOString() }
        ]
      }));
      
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Error sending comment:', err);
    }
  };

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    sectionRefs[sectionId]?.current?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
  };

  const toggleExpand = (itemId) => {
    setExpandedItems(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  // Clean and format content function
  const cleanAndFormatContent = (text, maxChars = 600) => {
    if (!text) return null;
    
    let cleaned = text
      .replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/\*(.+?)\*/g, '$1')
      .replace(/^(HEADLINE|SUBHEADLINE|CTA|INTRODUCCIÓN|CUERPO|ASUNTO|TESTIMONIOS?|BENEFICIOS?|URGENCIA|OFERTA|GARANTÍA|P\.S\.|APERTURA|CIERRE):\s*/gim, '')
      .replace(/^[🎯📢🔥💡✅❌🚀💰🎁📌🔑⚡️]\s*/gm, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
    
    const truncated = maxChars && cleaned.length > maxChars 
      ? cleaned.substring(0, maxChars) 
      : cleaned;
    
    const lines = truncated.split('\n').filter(l => l.trim());
    
    return lines.map((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return null;
      
      // Generate stable key based on content
      const lineKey = `preview-${i}-${trimmed.slice(0, 15).replace(/[^a-zA-Z0-9]/g, '')}`;
      
      if (i === 0 && trimmed.length > 20) {
        return (
          <h2 key={lineKey} className="text-xl font-bold text-white mb-3">
            {trimmed}
          </h2>
        );
      }
      
      if (trimmed.startsWith('•') || trimmed.startsWith('-') || trimmed.startsWith('✓')) {
        return (
          <div key={lineKey} className="flex items-start gap-2 mb-1">
            <span className="text-green-400 mt-0.5 shrink-0">✓</span>
            <span className="text-gray-300 text-sm">
              {trimmed.replace(/^[•\-✓]\s*/, '')}
            </span>
          </div>
        );
      }
      
      if (trimmed === trimmed.toUpperCase() && trimmed.length < 50 && trimmed.length > 3) {
        return (
          <h3 key={lineKey} className="text-sm font-semibold text-purple-400 uppercase tracking-wider mt-4 mb-2">
            {trimmed}
          </h3>
        );
      }
      
      return (
        <p key={lineKey} className="text-gray-300 text-sm leading-relaxed mb-2">
          {trimmed}
        </p>
      );
    }).filter(Boolean);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-purple-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Cargando preview...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-4">
        <div className="bg-[#1a1a24] border border-red-500/30 rounded-2xl p-8 max-w-md text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Enlace no válido</h1>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  const { brief, landings, sequences, ad_copies, creatives, counts, agency_name, funnel_name, approvals } = previewData;
  const whatsappMessages = previewData.whatsapp || [];

  const navSections = [
    { id: 'landings', icon: FileText, label: 'Landings', count: counts?.landings || 0, color: 'text-blue-400' },
    { id: 'emails', icon: Mail, label: 'Emails', count: counts?.emails || 0, color: 'text-amber-400' },
    { id: 'whatsapp', icon: MessageCircle, label: 'WhatsApp', count: whatsappMessages.length || 0, color: 'text-green-400' },
    { id: 'ads', icon: Megaphone, label: 'Copies de Ads', count: counts?.ad_copies || 0, color: 'text-emerald-400' },
    { id: 'creatives', icon: ImageIcon, label: 'Creatividades', count: counts?.creatives || 0, color: 'text-pink-400' }
  ];

  const approvedCount = ['landings', 'emails', 'ads', 'creatives'].filter(s => approvals?.[s]?.approved).length;

  // Browser Frame Component
  const BrowserFrame = ({ url, children }) => (
    <div className="bg-[#1a1a24] rounded-lg overflow-hidden border border-[#2a2a3a]">
      <div className="bg-[#12121a] px-3 py-2 flex items-center gap-2 border-b border-[#2a2a3a]">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-500/80" />
          <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
          <div className="w-3 h-3 rounded-full bg-green-500/80" />
        </div>
        <div className="flex-1 ml-2">
          <div className="bg-[#0a0a0f] rounded px-3 py-1 text-xs text-gray-500 max-w-xs truncate">
            {url}
          </div>
        </div>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );

  // Email Frame Component
  const EmailFrame = ({ from, subject, children }) => (
    <div className="bg-[#1a1a24] rounded-lg overflow-hidden border border-[#2a2a3a]">
      <div className="bg-[#12121a] px-4 py-3 border-b border-[#2a2a3a] space-y-1">
        <div className="flex items-center text-xs">
          <span className="text-gray-500 w-12">De:</span>
          <span className="text-gray-300">{from}</span>
        </div>
        <div className="flex items-center text-xs">
          <span className="text-gray-500 w-12">Para:</span>
          <span className="text-gray-300">Tu cliente</span>
        </div>
        <div className="flex items-center text-xs">
          <span className="text-gray-500 w-12">Asunto:</span>
          <span className="text-white font-medium">{subject}</span>
        </div>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );

  // WhatsApp Bubble Component
  const WhatsAppBubble = ({ message, sender }) => (
    <div className="flex justify-end mb-3">
      <div className="bg-[#005C4B] rounded-lg rounded-tr-none max-w-[80%] p-3 relative">
        <p className="text-xs text-green-300 font-medium mb-1">{sender}</p>
        <p className="text-white text-sm">{message}</p>
        <div className="flex items-center justify-end gap-1 mt-1">
          <span className="text-[10px] text-green-300/70">10:3{Math.floor(Math.random() * 10)}</span>
          <span className="text-green-300/70 text-xs">✓✓</span>
        </div>
      </div>
    </div>
  );

  // Section Header Component
  const SectionHeader = ({ icon: Icon, title, description, isApproved, color }) => (
    <div className="flex items-start justify-between mb-6">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${color.replace('text-', '')}/20`}
          style={{ backgroundColor: `${color === 'text-blue-400' ? '#3B82F6' : color === 'text-amber-400' ? '#F59E0B' : color === 'text-green-400' ? '#22C55E' : color === 'text-emerald-400' ? '#10B981' : '#EC4899'}15` }}>
          <Icon className={`w-6 h-6 ${color}`} />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white">{title}</h2>
          <p className="text-sm text-gray-400">{description}</p>
        </div>
      </div>
      {isApproved ? (
        <span className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/10 border border-green-500/30 rounded-full text-green-400 text-sm">
          <CheckCircle2 className="w-4 h-4" />
          Aprobada
        </span>
      ) : (
        <span className="px-3 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-400 text-sm">
          Pendiente de revisión
        </span>
      )}
    </div>
  );

  // Approval Controls Component
  const ApprovalControls = ({ section }) => {
    const isApproved = approvals?.[section]?.approved;
    
    if (isApproved) {
      return (
        <div className="mt-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl flex items-center gap-3">
          <CheckCircle2 className="w-6 h-6 text-green-400" />
          <div>
            <p className="text-green-400 font-medium">Sección aprobada</p>
            <p className="text-green-400/60 text-xs">
              {approvals[section]?.approved_at && new Date(approvals[section].approved_at).toLocaleString()}
            </p>
          </div>
        </div>
      );
    }
    
    return (
      <div className="mt-6 p-4 bg-amber-500/5 border border-amber-500/20 rounded-xl space-y-4">
        <p className="text-white font-medium">¿Esta sección está lista?</p>
        
        <Textarea
          value={comments[section] || ''}
          onChange={(e) => setComments(prev => ({ ...prev, [section]: e.target.value }))}
          placeholder="Escribe aquí si hay algo que cambiar..."
          className="bg-[#12121a] border-[#2a2a3a] text-white placeholder:text-gray-500 min-h-[80px] resize-none"
        />
        
        <div className="flex gap-3">
          <Button
            onClick={() => handleApprove(section)}
            disabled={approving[section]}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white h-11"
          >
            {approving[section] ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Check className="w-4 h-4 mr-2" />
            )}
            Aprobar sección
          </Button>
          
          {comments[section]?.trim() && (
            <Button
              onClick={() => handleComment(section)}
              variant="outline"
              className="border-[#2a2a3a] text-gray-300 hover:bg-[#2a2a3a] h-11"
            >
              <Send className="w-4 h-4 mr-2" />
              Solicitar cambios
            </Button>
          )}
        </div>
      </div>
    );
  };

  // Empty State Component
  const EmptyState = ({ icon: Icon, message }) => (
    <div className="text-center py-12">
      <Icon className="w-12 h-12 text-gray-600 mx-auto mb-3" />
      <p className="text-gray-500">{message}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      {/* Confetti CSS */}
      <style>{`
        @keyframes confetti {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        .confetti {
          position: fixed;
          width: 10px;
          height: 10px;
          top: -10px;
          animation: confetti 3s ease-out forwards;
        }
      `}</style>

      {/* Confetti particles when all approved */}
      {allApproved && (
        <div className="fixed inset-0 pointer-events-none z-50">
          {[...Array(20)].map((_, i) => (
            <div
              key={`confetti-${i}`}
              className="confetti"
              style={{
                left: `${Math.random() * 100}%`,
                backgroundColor: ['#22C55E', '#3B82F6', '#F59E0B', '#EC4899', '#8B5CF6'][i % 5],
                animationDelay: `${Math.random() * 2}s`,
                borderRadius: Math.random() > 0.5 ? '50%' : '0'
              }}
            />
          ))}
        </div>
      )}

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-[#0a0a0f]/90 backdrop-blur-lg border-b border-[#2a2a3a] z-40">
        <div className="h-full max-w-7xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              <span className="font-semibold text-white text-sm">Launch AI</span>
            </div>
            <div className="w-px h-6 bg-[#2a2a3a]" />
            <div>
              <p className="text-xs text-purple-400">{agency_name}</p>
              <p className="text-sm font-semibold text-white">{funnel_name}</p>
            </div>
          </div>

          {/* Progress */}
          <div className="hidden md:flex items-center gap-3">
            <span className="text-xs text-gray-400">{approvedCount} de 4 secciones</span>
            <div className="flex gap-1">
              {['landings', 'emails', 'ads', 'creatives'].map((s) => (
                <div
                  key={s}
                  className={`w-8 h-2 rounded-full transition-colors ${
                    approvals?.[s]?.approved ? 'bg-green-500' : 'bg-[#2a2a3a]'
                  }`}
                />
              ))}
            </div>
          </div>

          <div>
            {allApproved ? (
              <span className="px-3 py-1.5 bg-green-500/10 border border-green-500/30 rounded-full text-green-400 text-sm flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4" />
                Todo aprobado
              </span>
            ) : (
              <span className="text-sm text-gray-500">Revisando...</span>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Tabs */}
      <div className="md:hidden fixed top-16 left-0 right-0 bg-[#0a0a0f]/95 backdrop-blur-lg border-b border-[#2a2a3a] z-30 overflow-x-auto">
        <div className="flex px-2 py-2 gap-1">
          {navSections.map((nav) => (
            <button
              key={nav.id}
              onClick={() => scrollToSection(nav.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg whitespace-nowrap text-sm transition-colors ${
                activeSection === nav.id
                  ? 'bg-purple-500/20 text-purple-400'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <nav.icon className="w-4 h-4" />
              <span>{nav.label}</span>
              {approvals?.[nav.id]?.approved && (
                <CheckCircle2 className="w-3 h-3 text-green-400" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Main Layout */}
      <div className="flex pt-16">
        {/* Sidebar - Desktop */}
        <aside className="hidden md:block fixed left-0 top-16 bottom-0 w-60 bg-[#0a0a0f] border-r border-[#2a2a3a] p-6 overflow-y-auto">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-4">
            Contenido del funnel
          </p>
          
          <nav className="space-y-1">
            {navSections.map((nav) => (
              <button
                key={nav.id}
                onClick={() => scrollToSection(nav.id)}
                className={`w-full flex items-center justify-between p-3 rounded-lg transition-all ${
                  activeSection === nav.id
                    ? 'bg-purple-500/10 border-l-2 border-purple-500'
                    : 'hover:bg-[#1a1a24]'
                }`}
              >
                <div className="flex items-center gap-3">
                  <nav.icon className={`w-4 h-4 ${nav.color}`} />
                  <span className={`text-sm ${activeSection === nav.id ? 'text-white' : 'text-gray-400'}`}>
                    {nav.label}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">{nav.count}</span>
                  {approvals?.[nav.id]?.approved && (
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />
                  )}
                </div>
              </button>
            ))}
          </nav>

          {/* Brief Summary in Sidebar */}
          {brief && (
            <div className="mt-6 p-4 bg-[#1a1a24] rounded-xl">
              <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-3">Brief</p>
              <div className="space-y-2 text-xs">
                <div>
                  <span className="text-gray-500">Producto:</span>
                  <p className="text-white truncate">{brief.product_name || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Precio:</span>
                  <p className="text-white">{brief.price || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Nicho:</span>
                  <p className="text-white truncate">{brief.niche || '-'}</p>
                </div>
              </div>
            </div>
          )}
        </aside>

        {/* Main Content */}
        <main className="flex-1 md:ml-60 p-4 md:p-8 pt-20 md:pt-8 space-y-12">
          {/* All Approved Banner */}
          {allApproved && (
            <div className="bg-gradient-to-r from-green-600 to-green-500 rounded-2xl p-8 text-center">
              <CheckCircle2 className="w-16 h-16 text-white mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">
                🎉 ¡Has aprobado el funnel completo!
              </h2>
              <p className="text-green-100">
                {agency_name} ha sido notificado de tu aprobación
              </p>
              <p className="text-green-200/60 text-sm mt-2">{funnel_name}</p>
            </div>
          )}

          {/* LANDINGS SECTION */}
          <section ref={sectionRefs.landings} className="scroll-mt-24">
            <SectionHeader
              icon={FileText}
              title="Páginas de Landing"
              description="Páginas de captura y venta para tu funnel"
              isApproved={approvals?.landings?.approved}
              color="text-blue-400"
            />
            
            {landings && landings.length > 0 ? (
              <div className="space-y-4">
                {landings.map((landing, index) => {
                  const itemId = `landing-${index}`;
                  const isExpanded = expandedItems[itemId];
                  const content = landing.content?.copy || landing.content || '';
                  const pageType = landing.content?.page_type || landing.name || `Landing ${index + 1}`;
                  
                  return (
                    <BrowserFrame 
                      key={`landing-preview-${index}-${pageType}`} 
                      url={`mifunnel.com/${pageType.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <div className="mb-3 pb-3 border-b border-[#2a2a3a]">
                        <span className="text-purple-400 text-xs">Página {index + 1}</span>
                        <h3 className="text-white font-semibold">{pageType}</h3>
                      </div>
                      
                      <div className="prose prose-invert prose-sm max-w-none">
                        {cleanAndFormatContent(
                          typeof content === 'string' ? content : JSON.stringify(content),
                          isExpanded ? null : 600
                        )}
                      </div>
                      
                      {content.length > 600 && (
                        <button
                          onClick={() => toggleExpand(itemId)}
                          className="mt-3 text-purple-400 text-sm hover:text-purple-300 flex items-center gap-1"
                        >
                          {isExpanded ? 'Ver menos' : 'Ver completo'}
                          <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>
                      )}
                    </BrowserFrame>
                  );
                })}
              </div>
            ) : (
              <EmptyState icon={FileText} message="No hay landings generadas" />
            )}
            
            <ApprovalControls section="landings" />
          </section>

          {/* EMAILS SECTION */}
          <section ref={sectionRefs.emails} className="scroll-mt-24">
            <SectionHeader
              icon={Mail}
              title="Secuencia de Emails"
              description="Emails de nurturing y venta"
              isApproved={approvals?.emails?.approved}
              color="text-amber-400"
            />
            
            {sequences && sequences.length > 0 ? (
              <div className="space-y-4">
                {sequences.map((sequence, seqIndex) => (
                  <div key={sequence.id || `seq-${seqIndex}`}>
                    {sequence.emails?.map((email, emailIndex) => {
                      const itemId = `email-${seqIndex}-${emailIndex}`;
                      const isExpanded = expandedItems[itemId];
                      const content = email.body || email.content || '';
                      const subject = email.subject || content.split('\n')[0]?.substring(0, 50) || 'Sin asunto';
                      
                      return (
                        <EmailFrame
                          key={email.id || itemId}
                          from={`${agency_name} <hola@agencia.com>`}
                          subject={subject}
                        >
                          <div className="prose prose-invert prose-sm max-w-none">
                            {cleanAndFormatContent(content, isExpanded ? null : 400)}
                          </div>
                          
                          {content.length > 400 && (
                            <button
                              onClick={() => toggleExpand(itemId)}
                              className="mt-3 text-amber-400 text-sm hover:text-amber-300 flex items-center gap-1"
                            >
                              {isExpanded ? 'Ver menos' : 'Ver email completo'}
                              <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                            </button>
                          )}
                        </EmailFrame>
                      );
                    })}
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState icon={Mail} message="No hay secuencias de email" />
            )}
            
            <ApprovalControls section="emails" />
          </section>

          {/* WHATSAPP SECTION */}
          <section ref={sectionRefs.whatsapp} className="scroll-mt-24">
            <SectionHeader
              icon={MessageCircle}
              title="Mensajes de WhatsApp"
              description="Mensajes de seguimiento y recordatorios"
              isApproved={approvals?.whatsapp?.approved}
              color="text-green-400"
            />
            
            {whatsappMessages.length > 0 ? (
              <div className="bg-[#0B141A] rounded-xl p-4 border border-[#2a2a3a]">
                <div className="bg-[#1F2C34] rounded-lg p-3 mb-4">
                  <p className="text-white font-medium text-sm">{funnel_name}</p>
                  <p className="text-gray-400 text-xs">Chat de negocios</p>
                </div>
                
                <div className="space-y-3">
                  {whatsappMessages.map((msg, index) => (
                    <WhatsAppBubble
                      key={`whatsapp-${index}-${msg.content?.slice(0,10) || ''}`}
                      message={msg.content || msg.message || msg.text || 'Mensaje sin contenido'}
                      sender={agency_name}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <EmptyState icon={MessageCircle} message="No hay mensajes de WhatsApp" />
            )}
            
            <ApprovalControls section="whatsapp" />
          </section>

          {/* ADS COPIES SECTION */}
          <section ref={sectionRefs.ads} className="scroll-mt-24">
            <SectionHeader
              icon={Megaphone}
              title="Copies de Anuncios"
              description="Variantes de copy para campañas publicitarias"
              isApproved={approvals?.ads?.approved}
              color="text-emerald-400"
            />
            
            {ad_copies && ad_copies.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-4">
                {ad_copies.map((copy, index) => {
                  const angleColors = {
                    problema: 'bg-red-500/10 text-red-400 border-red-500/30',
                    solucion: 'bg-green-500/10 text-green-400 border-green-500/30',
                    testimonio: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
                    curiosidad: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
                    urgencia: 'bg-amber-500/10 text-amber-400 border-amber-500/30'
                  };
                  const angle = copy.type?.toLowerCase() || 'variante';
                  const colorClass = angleColors[angle] || 'bg-gray-500/10 text-gray-400 border-gray-500/30';
                  
                  return (
                    <div key={`copy-${index}-${copy.type || ''}`} className="bg-[#1a1a24] rounded-xl p-5 border border-[#2a2a3a]">
                      <div className="flex items-center justify-between mb-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${colorClass}`}>
                          {copy.type || 'Variante'}
                        </span>
                        <span className="text-[10px] text-gray-500 uppercase">Meta Ads</span>
                      </div>
                      
                      {copy.headline && (
                        <h4 className="text-white font-bold mb-2">{copy.headline}</h4>
                      )}
                      
                      {copy.text && (
                        <p className="text-gray-300 text-sm mb-3">{copy.text}</p>
                      )}
                      
                      {copy.cta && (
                        <span className="inline-block px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium">
                          {copy.cta}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState icon={Megaphone} message="No hay copies de anuncios" />
            )}
            
            <ApprovalControls section="ads" />
          </section>

          {/* CREATIVES SECTION */}
          <section ref={sectionRefs.creatives} className="scroll-mt-24">
            <SectionHeader
              icon={ImageIcon}
              title="Creatividades"
              description="Imágenes y videos para anuncios"
              isApproved={approvals?.creatives?.approved}
              color="text-pink-400"
            />
            
            {creatives && creatives.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {creatives.map((creative, index) => (
                  <div key={`creative-${creative.id || index}`} className="bg-[#1a1a24] rounded-xl overflow-hidden border border-[#2a2a3a]">
                    {creative.formats && Object.entries(creative.formats).map(([format, url]) => (
                      <div key={format} className="relative aspect-square">
                        {url?.startsWith('data:video') || url?.endsWith('.mp4') ? (
                          <video 
                            src={url} 
                            className="w-full h-full object-cover"
                            controls
                          />
                        ) : (
                          <img 
                            src={url} 
                            alt={`Creative ${format}`}
                            className="w-full h-full object-cover"
                          />
                        )}
                        <div className="absolute bottom-2 left-2 flex gap-1">
                          <span className="px-2 py-1 bg-black/70 rounded text-[10px] text-white">
                            {format}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState icon={ImageIcon} message="No hay creatividades generadas" />
            )}
            
            <ApprovalControls section="creatives" />
          </section>

          {/* Comments History */}
          {previewData.comments && previewData.comments.length > 0 && (
            <section className="bg-[#1a1a24] border border-[#2a2a3a] rounded-xl p-6">
              <button
                onClick={() => toggleExpand('comments-history')}
                className="w-full flex items-center justify-between"
              >
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-purple-400" />
                  Comentarios enviados ({previewData.comments.length})
                </h3>
                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${expandedItems['comments-history'] ? 'rotate-180' : ''}`} />
              </button>
              
              {expandedItems['comments-history'] && (
                <div className="mt-4 space-y-3">
                  {previewData.comments.map((comment, index) => (
                    <div key={`comment-${comment.created_at || index}`} className="bg-[#12121a] rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-purple-400 capitalize">{comment.section}</span>
                        <span className="text-xs text-gray-500">
                          {new Date(comment.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-gray-300 text-sm">{comment.comment}</p>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}
        </main>
      </div>

      {/* Footer */}
      <footer className="md:ml-60 border-t border-[#2a2a3a] py-6 mt-12">
        <div className="text-center text-xs text-gray-600">
          Powered by Launch AI
        </div>
      </footer>
    </div>
  );
};

export default FunnelPreview;
